import React, { useState, useEffect } from 'react';
import CommonDataTable from '../../Common/CommonDataTable';
import LoadingSpinner from '../../Common/LoadingSpinner';

const SRNAllocationGrid = ({
    supplier,
    srnAllocations = [],
    onSRNAllocationsChange,
    errors = {},
    loading = false,
    onLoadAvailableSRNs
}) => {
    const [availableSRNs, setAvailableSRNs] = useState([]);
    const [loadingSRNs, setLoadingSRNs] = useState(false);
    const [showAddSRN, setShowAddSRN] = useState(false);

    // Load available SRNs when supplier changes
    useEffect(() => {
        if (supplier && onLoadAvailableSRNs) {
            loadAvailableSRNs();
        }
    }, [supplier]);

    const loadAvailableSRNs = async () => {
        if (!supplier || !onLoadAvailableSRNs) return;

        setLoadingSRNs(true);
        try {
            const srns = await onLoadAvailableSRNs(supplier);
            setAvailableSRNs(srns);
        } catch (error) {
            console.error('Error loading available SRNs:', error);
        } finally {
            setLoadingSRNs(false);
        }
    };

    const handleAmountChange = (index, field, value) => {
        const updatedAllocations = [...srnAllocations];
        updatedAllocations[index] = {
            ...updatedAllocations[index],
            [field]: parseFloat(value) || 0
        };
        // Calculate remaining balance
        if (field === 'amountSetoff') {
            updatedAllocations[index].remainingBalance =
                updatedAllocations[index].setoffAmount - (parseFloat(value) || 0);
        }
        onSRNAllocationsChange(updatedAllocations);
    };

    const handleNotesChange = (index, notes) => {
        const updatedAllocations = [...srnAllocations];
        updatedAllocations[index] = {
            ...updatedAllocations[index],
            notes
        };
        onSRNAllocationsChange(updatedAllocations);
    };

    const handleRemoveSRN = (index) => {
        const updatedAllocations = srnAllocations.filter((_, i) => i !== index);
        onSRNAllocationsChange(updatedAllocations);
    };

    const handleAddSRN = (srn) => {
        const newAllocation = {
            srnId: srn._id,
            srnNo: srn.returnNoteNumber,
            srnDate: srn.returnDate,
            returnValue: srn.totals.totalValue,
            setoffAmount: srn.paymentSetoff.remainingAmount || srn.returnValue,
            amountSetoff: 0,
            remainingBalance: srn.paymentSetoff.remainingAmount || srn.returnValue,
            notes: ''
        };

        onSRNAllocationsChange([...srnAllocations, newAllocation]);
        setShowAddSRN(false);
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString();
    };

    const columns = [
        {
            key: 'srnNo',
            header: 'SRN No.',
            width: '120px'
        },
        {
            key: 'srnDate',
            header: 'SRN Date',
            width: '100px',
            render: (value) => formatDate(value)
        },
        {
            key: 'returnValue',
            header: 'Return Value',
            width: '120px',
            render: (value) => parseFloat(value).toFixed(2)
        },
        {
            key: 'setoffAmount',
            header: 'Available Setoff',
            width: '140px',
            render: (value) => parseFloat(value).toFixed(2)
        },
        {
            key: 'amountSetoff',
            header: 'Amount Setoff',
            width: '140px',
            render: (value, row, index) => (
                <input
                    type="number"
                    value={value === 0 ? '0' : (value || '')}
                    onChange={(e) => handleAmountChange(index, 'amountSetoff', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                    max={row.setoffAmount}
                    step="0.01"
                />
            )
        },
        {
            key: 'remainingBalance',
            header: 'Remaining Balance',
            width: '140px',
            render: (value) => (
                <span className={`font-medium ${value > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                    {parseFloat(value).toFixed(2)}
                </span>
            )
        },
        {
            key: 'notes',
            header: 'Notes',
            render: (value, row, index) => (
                <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => handleNotesChange(index, e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Add notes..."
                />
            )
        }
    ];

    const availableSRNColumns = [
        {
            key: 'returnNoteNumber',
            header: 'SRN No.',
            width: '120px'
        },
        {
            key: 'returnDate',
            header: 'SRN Date',
            width: '100px',
            render: (value) => formatDate(value)
        },
        {
            key: 'returnValue',
            header: 'Return Value',
            width: '120px',
            render: (value, row) => {
                return parseFloat(row?.totals?.totalWithTax || row?.returnValue || 0).toFixed(2);
            }
        },
        {
            header: 'Available Setoff',
            width: '140px',
            render: (value, row) => parseFloat(row?.paymentSetoff?.remainingAmount || row?.availableSetoffAmount || row?.returnValue || 0).toFixed(2)
        }
    ];

    // Filter out already selected SRNs
    const filteredAvailableSRNs = availableSRNs.filter(srn =>
        !srnAllocations.some(allocation => allocation.srnId === srn._id)
    );

    const totalAmountSetoff = srnAllocations.reduce((sum, allocation) => sum + (allocation.amountSetoff || 0), 0);

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between bg-gradient-to-r from-indigo-600 to-purple-600 px-3 py-1.5">
                <span className="text-xs font-semibold text-white">Supplier Return Note Allocation</span>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-indigo-100">Total: {parseFloat(totalAmountSetoff).toFixed(2)}</span>
                    {supplier && (
                        <button
                            onClick={() => setShowAddSRN(true)}
                            className="flex items-center gap-1 px-2 py-0.5 bg-white/20 hover:bg-white/30 rounded text-xs text-white transition-colors"
                            disabled={loadingSRNs}
                        >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Add SRN
                        </button>
                    )}
                </div>
            </div>
            <div className="p-4">

            {errors.srnAllocations && (
                <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600 flex items-center">
                        <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {errors.srnAllocations}
                    </p>
                </div>
            )}

            {!supplier ? (
                <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                    <p className="text-lg font-medium">Select a supplier first</p>
                    <p className="text-sm">Choose a supplier to load available supplier return notes for setoff</p>
                </div>
            ) : srnAllocations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                    <p className="text-lg font-medium">No SRNs allocated</p>
                    <p className="text-sm">Click "Add SRN" to start allocating return note setoffs</p>
                </div>
            ) : (
                <CommonDataTable
                    data={srnAllocations}
                    columns={columns}
                    loading={loading}
                    emptyMessage="No SRN allocations"
                    customActions={[
                        {
                            label: 'Remove',
                            onClick: (row, index) => handleRemoveSRN(index),
                            icon: (
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            ),
                            color: 'text-red-600 hover:text-red-800'
                        }
                    ]}
                />
            )}

            {/* Add SRN Modal */}
            {showAddSRN && (
                <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
                    <div className="relative w-full max-w-4xl bg-white rounded-xl shadow-2xl border border-gray-100 max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 rounded-t-xl px-6 py-4 z-10">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-semibold text-white">
                                        Add Available Supplier Return Notes
                                    </h3>
                                </div>
                                <button
                                    onClick={() => setShowAddSRN(false)}
                                    className="w-7 h-7 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="p-6">
                            {loadingSRNs ? (
                                <div className="flex items-center justify-center py-8">
                                    <LoadingSpinner />
                                    <span className="ml-2 text-gray-600">Loading available SRNs...</span>
                                </div>
                            ) : filteredAvailableSRNs.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                    </svg>
                                    <p className="text-lg font-medium">No available SRNs</p>
                                    <p className="text-sm">All supplier return notes for this supplier are already allocated or used</p>
                                </div>
                            ) : (
                                <CommonDataTable
                                    data={filteredAvailableSRNs}
                                    columns={availableSRNColumns}
                                    emptyMessage="No available SRNs"
                                    onRowClick={handleAddSRN}
                                    customActions={[
                                        {
                                            label: 'Add',
                                            onClick: handleAddSRN,
                                            icon: (
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                </svg>
                                            ),
                                            color: 'text-green-600 hover:text-green-800'
                                        }
                                    ]}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
            </div>
        </div>
    );
};

export default SRNAllocationGrid;